/**
 * VistaGate.tsx — Plan 04-04 Task 2.
 *
 * Two responsibilities, kept together because they share the same lifecycle:
 *
 *   1. Per-frame camera-in-zone detector. Every useFrame tick, test the
 *      camera's current position against each vistaZone (squared-distance
 *      vs. radius²). First match wins. Writes currentVistaId to
 *      useSceneStore ONLY on change to avoid Zustand subscription churn
 *      (Pitfall #9 style defense).
 *
 *   2. WARNING 7 closure: if the camera leaves a vista while era='2026',
 *      auto-flip era back to '1925'. Without this, the user sees an empty
 *      scene — 1925 EraGroup is at opacity 0, 2026 overlay has no active
 *      vista, nothing renders. Implemented as a useEffect on
 *      (currentVistaId, era) so it fires exactly once per transition
 *      (Test 10 asserts this).
 *
 * Why these two in one component:
 *   VistaGate already owns the `currentVistaId` write side. Subscribing to
 *   the same state in a useEffect keeps the vista-transition lifecycle in
 *   one file — a separate component would be a second singleton to track
 *   and would fight for write ordering with this one.
 *
 * Returns null — side-effect-only component mounted inside <Canvas>.
 * 04-RESEARCH.md Pattern 5 + Open Question #1 recommendation.
 */
import { useFrame } from '@react-three/fiber';
import { useEffect } from 'react';
import { useSceneStore } from '../store/useSceneStore';
import { useManifest } from '../content/useManifest';

export function VistaGate(): null {
  const { manifest } = useManifest();
  const setCurrentVistaId = useSceneStore((s) => s.setCurrentVistaId);
  const currentVistaId = useSceneStore((s) => s.currentVistaId);
  const era = useSceneStore((s) => s.era);
  const setEra = useSceneStore((s) => s.setEra);

  // Per-frame zone detection. 3 vistas × 3 subtractions + 3 multiplications +
  // 1 comparison per frame = free. No spatial index needed at this count.
  useFrame(({ camera }) => {
    const zones = manifest?.vistaZones;
    if (!zones || zones.length === 0) return; // Pitfall #9

    const camPos = camera.position;
    let hit: string | null = null;
    for (const zone of zones) {
      const [zx, zy, zz] = zone.position;
      const dx = camPos.x - zx;
      const dy = camPos.y - zy;
      const dz = camPos.z - zz;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq < zone.radius * zone.radius) {
        hit = zone.id;
        break; // first match wins (zones don't overlap in practice)
      }
    }

    // Write only on change — avoids Zustand re-render churn (60 writes/sec
    // would flood every subscriber even when nothing actually changes)
    const current = useSceneStore.getState().currentVistaId;
    if (hit !== current) setCurrentVistaId(hit);
  });

  // WARNING 7 closure: if the camera leaves a vista while era='2026',
  // snap era back to '1925'. Why useEffect (not useFrame):
  //   - Fires exactly once per (currentVistaId, era) change — Test 10's
  //     "at most one call" guarantee
  //   - Reactive: no timer, no polling, no race with the EraGroup crossfade
  //   - useFrame would re-fire every frame while conditions hold, flooding
  //     Zustand subscribers and potentially double-toggling during the
  //     400ms crossfade window
  //
  // Entering a vista from 1925 is fine (user stays in 1925 unless they hit
  // the toggle); entering from 2026 is impossible (era can only reach 2026
  // via the in-vista toggle). So this is the only transition that needs
  // remediation.
  useEffect(() => {
    if (currentVistaId === null && era === '2026') {
      setEra('1925');
    }
  }, [currentVistaId, era, setEra]);

  return null;
}
